// What a service was authored to do, as labelled values for the transaction detail's Config row. Each kind lists
// its fields in a fixed order; a field that is unset, empty or false is left out.

import {
  ActionType,
  type AggregateServiceModel,
  type BatchServiceModel,
  type BinaryBodyContent,
  type BinaryReference,
  type ConcatServiceModel,
  type ExpressionServiceModel,
  type FieldValue,
  type FilterServiceModel,
  type FlattenLevel,
  type FlattenServiceModel,
  type GroupServiceModel,
  type HttpServiceModelBase,
  type HttpServiceModelPaging,
  type IBodyContent,
  type IServiceModel,
  type JavaScriptServiceModel,
  type JoinServiceModel,
  type MultipartFormDataBodyContent,
  type NextPageRequest,
  type Service,
  type SftpServiceModel,
  type SplitServiceModel,
  type SubWorkflowServiceModel,
  type TextBodyContent,
  type UrlEncodedBodyContent,
  type ValidatorRule,
} from "@/types";

/**
 * How a value is shown. `text` is plain; `slots` is monospace text that may hold `{{ }}` slots; `expression` is a
 * bare expression stored without braces; `code` is a multi-line template that may hold slots; `javascript` is
 * source code.
 */
export type ConfigFormat = "text" | "slots" | "expression" | "code" | "javascript";

export interface ConfigField {
  label: string;
  value: string;
  format: ConfigFormat;
}

type ServiceNames = Record<string, string>;

export function configFields(service: Service, serviceNames: Record<string, string>): ConfigField[] {
  const filter = service.serviceFilterExpression;
  const filterField: ConfigField = { label: "Filter when", value: filter, format: filter.includes("{{") ? "slots" : "expression" };
  const fields = [...kindFields(service.serviceModelType, serviceNames), filterField];
  return fields.filter((field) => field.value !== "");
}

// Every value below is a string, and an unset one is the empty string, so configFields can drop it in one place.
// The wire omits null properties, so an optional property arrives as undefined.
function kindFields(model: IServiceModel, names: ServiceNames): ConfigField[] {
  switch (model.$type) {
    case "http":
    case "webhook":
      return httpFields(model as HttpServiceModelBase, names);
    case "httpPaging":
      return [...httpFields(model as HttpServiceModelPaging, names), ...pagingFields(model as HttpServiceModelPaging)];
    case "sftp": {
      const sftp = model as SftpServiceModel;
      return [
        { label: "Operation", value: sftp.operation, format: "text" },
        { label: "Remote path", value: sftp.remotePath, format: "slots" },
        { label: "Remote file name", value: sftp.remoteFileName ?? "", format: "slots" },
        { label: "New path", value: sftp.newPath ?? "", format: "slots" },
        { label: "Overwrite", value: sftp.overwrite ? "Yes" : "", format: "text" },
        valueField("Source", sftp.source, names),
        { label: "Connection", value: sftp.connectionName ?? "", format: "text" },
      ];
    }
    case "subWorkflow": {
      const sub = model as SubWorkflowServiceModel;
      const inputs = Object.entries(sub.inputs).map(([key, value]) => `${key}: ${value}`);
      return [
        { label: "Workflow", value: sub.workflowId, format: "text" },
        { label: "Inputs", value: inputs.join("\n"), format: "slots" },
      ];
    }
    case "split":
      return [valueField("Source", (model as SplitServiceModel).source, names)];
    case "batch": {
      const batch = model as BatchServiceModel;
      return [
        valueField("Source", batch.source, names),
        { label: "Max rows", value: String(batch.maxRows ?? ""), format: "text" },
        { label: "Max bytes", value: String(batch.maxBytes ?? ""), format: "text" },
      ];
    }
    case "flatten": {
      const flatten = model as FlattenServiceModel;
      return [
        valueField("Source", flatten.source, names),
        { label: "Levels", value: flatten.levels.map(levelLine).join("\n"), format: "slots" },
      ];
    }
    case "group": {
      const group = model as GroupServiceModel;
      return [
        valueField("Source", group.source, names),
        { label: "Key", value: group.key.join("."), format: "slots" },
        { label: "Head", value: group.head.join(", "), format: "slots" },
        { label: "Fields", value: group.fields.join(", "), format: "slots" },
      ];
    }
    case "join":
    case "filter": {
      const match = model as JoinServiceModel | FilterServiceModel;
      return [
        valueField("In", match.in, names),
        { label: "In key", value: match.inKey.join("."), format: "slots" },
        { label: "Key", value: match.key, format: "slots" },
        { label: "Include", value: match.include, format: "text" },
      ];
    }
    case "concat":
      return (model as ConcatServiceModel).parts.map((part, index) => valueField(`Part ${index + 1}`, part, names));
    case "aggregate": {
      const aggregate = model as AggregateServiceModel;
      return [
        { label: "Path", value: aggregate.path.join("."), format: "slots" },
        { label: "Operation", value: aggregate.operation, format: "text" },
      ];
    }
    case "expression":
      return [{ label: "Expression", value: (model as ExpressionServiceModel).expression, format: "code" }];
    case "javaScript":
      return [{ label: "JavaScript", value: (model as JavaScriptServiceModel).javaScript, format: "javascript" }];
    case "rejoin":
    case "wait":
    case "return":
      return [];
    default:
      return modelProperties(model);
  }
}

function httpFields(http: HttpServiceModelBase, names: ServiceNames): ConfigField[] {
  const headers = Object.entries(http.requestHeaders).flatMap(([name, values]) => values.map((value) => `${name}: ${value}`));
  return [
    { label: "Method", value: http.httpMethod, format: "text" },
    { label: "URL", value: http.targetUrl, format: "slots" },
    { label: "Headers", value: headers.join("\n"), format: "slots" },
    { label: "Body", value: bodyText(http.bodyContent, names), format: "code" },
    { label: "Connection", value: http.connectionName ?? "", format: "text" },
    { label: "Validators", value: http.validatorPipeline.map(ruleLine).join("\n"), format: "text" },
  ];
}

function pagingFields(paging: HttpServiceModelPaging): ConfigField[] {
  return [
    { label: "Next page", value: nextPageLines(paging.nextPage), format: "expression" },
    { label: "Has next page", value: paging.hasNextPageExpression, format: "expression" },
    { label: "Max pages", value: String(paging.maxPages), format: "text" },
  ];
}

// One line per part the next page changes. Every value is a bare expression.
function nextPageLines(nextPage: NextPageRequest | undefined): string {
  if (!nextPage) return "";
  const lines: string[] = [];
  if (nextPage.url) lines.push(`url: ${nextPage.url}`);
  for (const [name, value] of Object.entries(nextPage.query)) lines.push(`query ${name}: ${value}`);
  if (nextPage.body) lines.push(`body: ${nextPage.body}`);
  return lines.join("\n");
}

function bodyText(body: IBodyContent | undefined, names: ServiceNames): string {
  if (!body) return "";
  switch (body.$type) {
    case "text":
      return (body as TextBodyContent).textContent;
    case "urlEncoded":
      return (body as UrlEncodedBodyContent).bodyFormUrlEncodedContent.map((pair) => `${pair.key}: ${pair.value}`).join("\n");
    case "multipart":
      return (body as MultipartFormDataBodyContent).multipartFormContent
        .map((part) => `${part.name}: ${fieldValueText(part.value, names)}`)
        .join("\n");
    case "binary":
      return referenceText((body as BinaryBodyContent).source, names);
    default:
      return "";
  }
}

// "Status 429: Retry, up to 3 times, every 5 s". The interval is absent when the engine's backoff picks it.
function ruleLine(rule: ValidatorRule): string {
  const action = rule.action;
  const line = `${rule.name}: ${action.type}`;
  if (action.type !== ActionType.Retry) return line;
  const limit = `${line}, up to ${action.maxRetries} times`;
  if (action.retryIntervalSeconds == null) return limit;
  return `${limit}, every ${action.retryIntervalSeconds} s`;
}

// "ExpenseEntriesList as expense", then the fields it keeps in parentheses when it names any.
function levelLine(level: FlattenLevel): string {
  const line = `${level.path.join(".")} as ${level.flattenedKey}`;
  if (level.fields.length === 0) return line;
  return `${line} (${level.fields.join(", ")})`;
}

// A binary reference reads as the referenced service's name; an expression as its template.
function valueField(label: string, value: FieldValue | undefined, names: ServiceNames): ConfigField {
  if (value?.$type === "binary") return { label, value: referenceText(value.reference, names), format: "text" };
  return { label, value: value?.expression ?? "", format: "slots" };
}

function fieldValueText(value: FieldValue, names: ServiceNames): string {
  if (value.$type === "binary") return referenceText(value.reference, names);
  return value.expression;
}

// "Get users › d.results": the service's name, or its id when the name is not known, then the path inside it.
function referenceText(reference: BinaryReference, names: ServiceNames): string {
  const name = names[reference.serviceId] ?? reference.serviceId;
  if (reference.path.length === 0) return name;
  return `${name} › ${reference.path.join(".")}`;
}

// A kind with no entry above lists its own set properties: `maxRows` becomes "Max rows", a primitive shows as
// itself and anything else as compact JSON.
function modelProperties(model: IServiceModel): ConfigField[] {
  return Object.entries(model)
    .filter(([key, value]) => key !== "$type" && !isEmpty(value))
    .map(([key, value]) => ({
      label: words(key),
      value: typeof value === "object" ? JSON.stringify(value) : String(value),
      format: "text" as const,
    }));
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === false || value === "") return true;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

function words(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
